import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../common/formkit/FormPageLayout";
import { Section, TextField, TextAreaField, CurrencyField, SegmentField } from "../../common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../common/formkit/formHelpers";
import { showToast } from "../../common/notification/Notification";
import {
  addCashbook,
  editCashbook,
  getCashbookById,
  getCashbookEntries,
  addCashbookEntry,
  editCashbookEntry,
} from "../../../api/tools/toolsApi";
import { CASHBOOK_HOME, cashbookListUrl, useToolsToken } from "./toolFormHelpers";

const BOOK_FORM_ID = "cashbook-form";
const ENTRY_FORM_ID = "cashbook-entry-form";

const bookSchema = Yup.object({
  name: Yup.string().trim().required("Please enter a cashbook name"),
});

const entrySchema = Yup.object({
  entry_type: Yup.string().oneOf(["CREDIT", "DEBIT"]).required(),
  amount: Yup.number().typeError("Please enter a valid amount").moreThan(0, "Please enter a valid amount").required("Please enter a valid amount"),
});

const ENTRY_TYPES = [
  { value: "CREDIT", label: "Cash In (Credit)" },
  { value: "DEBIT", label: "Cash Out (Debit)" },
];

const NotFound = ({ text, onBack }) => (
  <div className="fk-page" style={{ maxWidth: 1200 }}>
    <div className="alert alert-warning d-flex align-items-center justify-content-between" role="alert">
      <span>{text}</span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onBack}>
        Back to Cashbook
      </button>
    </div>
  </div>
);

const errorText = (err, fallback) => err?.response?.data?.text || err?.response?.data?.message || err?.message || fallback;

// The API answers 200 with statusType FAILURE for business errors
const assertSuccess = (res, fallback) => {
  if (res?.statusType && res.statusType !== "SUCCESS") throw new Error(res.text || fallback);
  return res;
};

/* ---------------- Create / edit a cashbook ---------------- */

const BookFormBody = ({ saved }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);
  return (
    <Form id={BOOK_FORM_ID} noValidate>
      <FocusFirstError />
      <Section title="Cashbook details" hint="Name it after the site, person or purpose the cash belongs to.">
        <div className="fk-grid">
          <TextField
            name="name"
            label="Cashbook Name"
            required
            placeholder="e.g. Site Cashbook, Petty Cash, Daily Office"
            className="fk-col-full"
          />
          <TextAreaField
            name="description"
            label="Description"
            rows={3}
            placeholder="e.g. Daily site expenses, tea, wages, materials"
            className="fk-col-full"
          />
        </div>
      </Section>
    </Form>
  );
};

/** /partner/tools/cashbook/new and /partner/tools/cashbook/:bookId/edit (replaces the cashbook popup) */
export const CashbookFormPage = () => {
  const { bookId } = useParams();
  const isEdit = Boolean(bookId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = useToolsToken();
  const exit = useExitWizard(isEdit ? cashbookListUrl(bookId) : CASHBOOK_HOME);

  const [book, setBook] = useState(location.state?.cashbook || null);
  const [loading, setLoading] = useState(isEdit && !location.state?.cashbook);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || book || !token) return undefined;
    let cancelled = false;
    getCashbookById(token, bookId)
      .then((data) => {
        if (cancelled) return;
        const found = data?.cashBook;
        if (found?.id) setBook(found);
        else setNotFound(true);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isEdit, book, token, bookId]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = { book_name: values.name, description: values.description };
      let targetId = bookId;
      if (isEdit) {
        assertSuccess(await editCashbook(token, { id: book?.id ?? bookId, ...body }), "Failed to save cashbook");
      } else {
        const res = assertSuccess(await addCashbook(token, body), "Failed to save cashbook");
        targetId = res?.cashBook?.id;
      }
      showToast(isEdit ? "Cashbook updated successfully" : "Cashbook created successfully", { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(cashbookListUrl(targetId), { replace: true }), 0);
    } catch (err) {
      // Form data stays as entered
      showToast(errorText(err, "Failed to save cashbook"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This cashbook could not be found. It may have been removed." onBack={() => navigate(CASHBOOK_HOME)} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Cashbook" : "Add New Cashbook"}
      subtitle={isEdit ? "Update the name or description of this cashbook." : "Create a cashbook to start tracking cash in and cash out."}
      formId={BOOK_FORM_ID}
      submitLabel={isEdit ? "Update" : "Create Cashbook"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik
        initialValues={{ name: book?.book_name || "", description: book?.description || "" }}
        validationSchema={bookSchema}
        onSubmit={handleSubmit}
      >
        <BookFormBody saved={saved} />
      </Formik>
    </FormPageLayout>
  );
};

/* ---------------- Add / edit a cashbook entry ---------------- */

const EntryFormBody = ({ saved }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);
  return (
    <Form id={ENTRY_FORM_ID} noValidate>
      <FocusFirstError />
      <Section title="Entry details" hint="Record money coming in or going out of this cashbook.">
        <div className="fk-grid">
          <SegmentField name="entry_type" label="Entry Type" required options={ENTRY_TYPES} className="fk-col-full" />
          <CurrencyField name="amount" label="Amount" required placeholder="e.g. 2,500" />
        </div>
      </Section>
    </Form>
  );
};

/** /partner/tools/cashbook/:bookId/entries/new and /…/entries/:entryId/edit (replaces the entry popup) */
export const CashbookEntryFormPage = () => {
  const { bookId, entryId } = useParams();
  const isEdit = Boolean(entryId);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useToolsToken();
  const exit = useExitWizard(cashbookListUrl(bookId));

  const [entry, setEntry] = useState(location.state?.entry || null);
  const [loading, setLoading] = useState(isEdit && !location.state?.entry);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || entry || !token) return undefined;
    let cancelled = false;
    // Direct link / refresh: find the entry in the cashbook's entries
    getCashbookEntries(token, bookId, { page_number: 1, page_size: 1000 })
      .then((data) => {
        if (cancelled) return;
        const list = data?.cashBookEntries || [];
        const found = list.find((e) => String(e.id) === String(entryId));
        if (found) setEntry(found);
        else setNotFound(true);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isEdit, entry, token, bookId, entryId]);

  const entryType = String(entry?.entry_type || "").toUpperCase();
  const initialType = isEdit
    ? entryType === "DEBIT"
      ? "DEBIT"
      : "CREDIT"
    : searchParams.get("type") === "DEBIT"
      ? "DEBIT"
      : "CREDIT";

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = { cashbook_id: Number(bookId) || bookId, entry_type: values.entry_type, amount: Number(values.amount) };
      assertSuccess(
        isEdit
          ? await editCashbookEntry(token, { id: entry?.id ?? entryId, ...body })
          : await addCashbookEntry(token, body),
        "Failed to save entry"
      );
      showToast(isEdit ? "Cashbook entry updated" : "Entry added to Cashbook", { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(cashbookListUrl(bookId), { replace: true }), 0);
    } catch (err) {
      showToast(errorText(err, "Failed to save entry"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This entry could not be found. It may have been removed." onBack={() => navigate(cashbookListUrl(bookId))} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Entry" : "Add Entry to Cashbook"}
      subtitle={isEdit ? "Update this cash entry." : "Record a Cash In or Cash Out transaction."}
      formId={ENTRY_FORM_ID}
      submitLabel={isEdit ? "Update Entry" : "Save Entry"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik
        initialValues={{ entry_type: initialType, amount: entry?.amount ?? "" }}
        validationSchema={entrySchema}
        onSubmit={handleSubmit}
      >
        <EntryFormBody saved={saved} />
      </Formik>
    </FormPageLayout>
  );
};
